/**
 * Het beginscherm van de medewerker draagt de naam en het pictogram van zijn werkgever
 * (Stap 12, uitgebreid in Extra 1).
 *
 * Een pictogram met "UP2D8" eronder zegt een operator niets; "Janssens" wel. Daarom wordt het
 * statische manifest na het aanmelden vervangen door dat van zijn bedrijf.
 *
 * **Sinds Extra 1 komt dat manifest van de server** (sinds MRK-4
 * `GET /api/v1/manifest.webmanifest?tenant=:code`) in plaats van uit een blob die de client
 * zelf samenstelde. Twee redenen, en de tweede is de echte:
 *
 * 1. De pictogrammen van de klant staan erin. Die worden door `media` uit zijn eigen bestand
 *    gemaakt, in de maten die een beginscherm vraagt; de client kan dat niet.
 * 2. **Relatieve paden in een blob-manifest lossen op tegen de blob-URL.** `start_url: "/app"` en
 *    een pictogram op `/icon-192.png` wijzen dan naar `blob:https://…/app` — een adres dat niet
 *    bestaat. Browsers verschillen in hoe streng ze daarover zijn, en dat is precies het soort
 *    verschil dat je pas op het toestel van een klant ontdekt.
 *
 * Twee dingen om te weten voordat je hier iets verwacht:
 *
 * - **Dit werkt op Android en in Chrome.** iOS leest het manifest één keer, op het moment dat iemand
 *   "Zet op beginscherm" kiest, en kijkt daarna niet meer. Wie op een iPhone al geïnstalleerd heeft,
 *   houdt de naam van toen. Dat is geen fout van ons en er is geen omweg voor.
 * - **Zonder eigen pictogram vallen de iconen terug op die van UP2D8.** Dat doet de server; een
 *   manifest dat naar een onbestaand bestand wijst, laat een toestel de installatie weigeren
 *   zonder uitleg.
 */

const LINK_ID = 'up2d8-manifest';

export function applyTenantManifest(tenant: { code: string }): void {
  if (!tenant.code) return;

  try {
    const existing = document.getElementById(LINK_ID) as HTMLLinkElement | null;
    // Een blob-URL uit een vorige versie van deze functie vrijgeven, zodat een app die dagen open
    // staat op een tablet aan de lijn geen geheugen vasthoudt.
    if (existing?.dataset.blob === 'true') URL.revokeObjectURL(existing.href);

    const link = existing ?? document.createElement('link');
    link.id = LINK_ID;
    link.rel = 'manifest';
    link.href = `/api/v1/manifest.webmanifest?tenant=${encodeURIComponent(tenant.code)}`;
    delete link.dataset.blob;
    if (!existing) document.head.append(link);

    // Het statische manifest weghalen, anders leest de browser die van de twee die hij het eerst
    // vindt — en dat is de verkeerde.
    document
      .querySelectorAll<HTMLLinkElement>(`link[rel="manifest"]:not(#${LINK_ID})`)
      .forEach((other) => other.remove());
  } catch {
    // Een browser met een strikt beleid houdt het statische manifest. Dat werkt, er staat alleen
    // "UP2D8" onder het pictogram.
  }
}
